/**
 * 同じ画面操作の再試行では同じ UUID、入力が変われば新しい UUID を返す。
 * Shopify の @idempotent は「同じ変数＋同じキー」を要求するため、エラー後に
 * フォームを直した操作へ古いキーを流用しないことも同じくらい重要。
 */
export interface IdempotencyAttempt {
  fingerprint: string;
  key: string;
}

export interface PersistentIdempotencyAttempt extends IdempotencyAttempt {
  /** Authoritative shop identity captured before the request was started. */
  shopGeneration: string;
}

interface StoredAttemptEnvelope {
  version: 1;
  shopGeneration: string;
  attempts: PersistentIdempotencyAttempt[];
}

export interface PersistentIdempotencyAttemptInput {
  scope: string;
  shopGeneration: string;
  previous: PersistentIdempotencyAttempt | null;
  fingerprint: string;
}

// The old prefix stored an unscoped array. Never inspect or migrate those
// values: assigning one to whichever shop happens to be open would turn a
// response-loss retry into a cross-tenant operation.
const STORAGE_PREFIX = "ub-idempotency-attempts:v2:";

function requireShopGeneration(shopGeneration: string): string {
  if (shopGeneration.trim() === "") {
    throw new Error("verified shop generation is required");
  }
  return shopGeneration;
}

function storageKey(scope: string, shopGeneration: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(shopGeneration)}:${scope}`;
}

function isAttempt(value: unknown): value is IdempotencyAttempt {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<IdempotencyAttempt>;
  return (
    typeof candidate.fingerprint === "string" &&
    typeof candidate.key === "string"
  );
}

function isPersistentAttempt(
  value: unknown,
  shopGeneration: string,
): value is PersistentIdempotencyAttempt {
  return (
    isAttempt(value) &&
    (value as Partial<PersistentIdempotencyAttempt>).shopGeneration ===
      shopGeneration
  );
}

function readStoredAttempts(
  scope: string,
  shopGeneration: string,
): PersistentIdempotencyAttempt[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey(scope, shopGeneration));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return [];
    const envelope = parsed as Partial<StoredAttemptEnvelope>;
    if (
      envelope.version !== 1 ||
      envelope.shopGeneration !== shopGeneration ||
      !Array.isArray(envelope.attempts) ||
      !envelope.attempts.every((attempt) =>
        isPersistentAttempt(attempt, shopGeneration),
      )
    ) {
      return [];
    }
    const fingerprints = new Set<string>();
    for (const attempt of envelope.attempts) {
      if (fingerprints.has(attempt.fingerprint)) return [];
      fingerprints.add(attempt.fingerprint);
    }
    return envelope.attempts;
  } catch {
    // Storage拒否・破損値のどちらでも、送信自体は止めず新しいattemptへ退避する。
    return [];
  }
}

function writeStoredAttempts(
  scope: string,
  shopGeneration: string,
  attempts: PersistentIdempotencyAttempt[],
): void {
  if (typeof window === "undefined") return;
  if (
    attempts.some((attempt) => attempt.shopGeneration !== shopGeneration)
  ) {
    throw new Error("idempotency attempt shop generation mismatch");
  }
  try {
    const key = storageKey(scope, shopGeneration);
    if (attempts.length === 0) {
      window.sessionStorage.removeItem(key);
    } else {
      const envelope: StoredAttemptEnvelope = {
        version: 1,
        shopGeneration,
        attempts,
      };
      window.sessionStorage.setItem(key, JSON.stringify(envelope));
    }
  } catch {
    // Safari private mode等で保存できなくても、メモリ内のsame-key retryは維持する。
  }
}

export function idempotencyAttempt(
  previous: IdempotencyAttempt | null,
  fingerprint: string,
): IdempotencyAttempt {
  if (previous?.fingerprint === fingerprint) return previous;
  return { fingerprint, key: crypto.randomUUID() };
}

/**
 * response-loss後のページ再読込でも、同じpayloadには同じキーを返す。
 *
 * fingerprintごとに保持するのは、未確定の操作Aから入力を変えたあと再びAへ
 * 戻った際、新しいキーで二重反映しないため。成功または副作用前と断定できる4xxで
 * 呼び出し側が明示的にclearするまで残す。
 */
export function persistentIdempotencyAttempt(
  input: PersistentIdempotencyAttemptInput,
): PersistentIdempotencyAttempt {
  const shopGeneration = requireShopGeneration(input.shopGeneration);
  const stored = readStoredAttempts(input.scope, shopGeneration);
  const matchingPrevious =
    input.previous?.shopGeneration === shopGeneration &&
    input.previous.fingerprint === input.fingerprint
      ? input.previous
      : (stored.find(
          (attempt) => attempt.fingerprint === input.fingerprint,
        ) ?? null);
  const attempt = matchingPrevious ?? {
    fingerprint: input.fingerprint,
    key: crypto.randomUUID(),
    shopGeneration,
  };
  const withoutSameFingerprint = stored.filter(
    (candidate) => candidate.fingerprint !== input.fingerprint,
  );
  writeStoredAttempts(input.scope, shopGeneration, [
    ...withoutSameFingerprint,
    attempt,
  ]);
  return attempt;
}

export function clearPersistentIdempotencyAttempt(
  scope: string,
  attempt: PersistentIdempotencyAttempt,
): void {
  const shopGeneration = requireShopGeneration(attempt.shopGeneration);
  const remaining = readStoredAttempts(scope, shopGeneration).filter(
    (candidate) =>
      candidate.fingerprint !== attempt.fingerprint ||
      candidate.key !== attempt.key,
  );
  writeStoredAttempts(scope, shopGeneration, remaining);
}
